import React, { useCallback, useEffect, useState } from 'react';
import CommonLoader from '../../../Components/CommonLoader';
import CommonTable from '../../../Components/CommonTable';
import NoDataPage from '../../../Components/NoDataPage';
import PageHeading from '../../../Components/PageHeading';
import StatusBadge from '../../../Components/StatusBadge';
import CommonActionMenu from '../../../Components/CommonActionMenu';
import { useToast } from '../../../Components/CommonToast';
import { useConfirm } from '../../../Components/ConfirmModal';
import { Body } from '../../../Constants/Fonts';
import {
    risk_portal_overdue_treatments,
    risk_portal_pending_treatments,
    risk_portal_approve_treatment,
    risk_portal_reject_treatment,
} from '../../../services';

const unwrap = (res) =>
    res?.data?.response_data?.data ?? res?.data?.data ??
    res?.statusCode?.response_data?.data ?? res?.statusCode?.data ?? null;

const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? '—'
        : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const riskId = (v) => <Body as="span" variant={2} color="var(--color-primary)" weight="semibold">{v || '—'}</Body>;
const strategyBadge = (s) => (s ? <StatusBadge variant="info" label={String(s).replace(/_/g, ' ')} /> : <Body as="span" variant={2} color="var(--color-text-muted)">—</Body>);

// Uppercase card caption (matches the dashboard widget titles).
const CardTitle = ({ children, count }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Body as="span" weight="semibold" color="var(--color-text-secondary)" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {children}
        </Body>
        {count > 0 && <StatusBadge variant="danger" label={String(count)} />}
    </div>
);

// TreatmentPlans — org-wide treatment tracking. Two sections: overdue treatment
// actions (past their due date) and treatment plans awaiting approval. Approvers
// (can_approve) can approve / reject pending plans inline.
//
// Props: mine (owner's "My Treatments" title variant), canApprove.
export default function TreatmentPlans({ mine = false, canApprove = false }) {
    const [overdue, setOverdue] = useState([]);
    const [pending, setPending] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const { notify, toastNode } = useToast();
    const { confirm, confirmModal } = useConfirm();

    const load = useCallback(async () => {
        setLoading(true);
        const [ov, pd] = (await Promise.allSettled([
            risk_portal_overdue_treatments(),
            risk_portal_pending_treatments(),
        ])).map((r) => (r.status === 'fulfilled' ? unwrap(r.value) : null));
        setOverdue(Array.isArray(ov) ? ov : []);
        setPending(Array.isArray(pd) ? pd : []);
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const approve = async (tp) => {
        setBusy(true);
        try {
            await risk_portal_approve_treatment(tp.id);
            notify('Treatment plan approved', 'success');
            await load();
        } catch (e) {
            notify(e?.response?.data?.error?.message || 'Failed to approve treatment plan', 'error');
        } finally { setBusy(false); }
    };

    const reject = async (tp) => {
        const ok = await confirm({
            title: 'Reject treatment plan',
            message: `Send ${tp.risk_id || 'this plan'} back for revision?`,
            confirmLabel: 'Reject', danger: true, themeColor: false,
        });
        if (!ok) return;
        setBusy(true);
        try {
            await risk_portal_reject_treatment(tp.id, {});
            notify('Treatment plan rejected', 'success');
            await load();
        } catch (e) {
            notify(e?.response?.data?.error?.message || 'Failed to reject treatment plan', 'error');
        } finally { setBusy(false); }
    };

    if (loading) return <CommonLoader fullPage label="Loading treatment plans…" />;

    const OVERDUE_HEADERS = ['Action', 'Risk', 'Strategy', 'Owner', 'Due', 'Days Overdue', 'Priority'];
    const overdueRows = overdue.map((a) => ({
        data: [
            <Body as="span" variant={2} color="var(--color-text-primary)">{a.title || '—'}</Body>,
            riskId(a.risk_id),
            strategyBadge(a.strategy),
            <Body as="span" variant={2} color="var(--color-text-secondary)">{a.owner_email || '—'}</Body>,
            <Body as="span" variant={2} color="var(--color-text-secondary)">{fmtDate(a.due_date)}</Body>,
            <Body as="span" variant={2} color="var(--color-danger)" weight="semibold">{a.days_overdue != null ? `${a.days_overdue}d` : '—'}</Body>,
            a.priority ? <StatusBadge status={a.priority} /> : <Body as="span" variant={2} color="var(--color-text-muted)">—</Body>,
        ],
    }));

    const PENDING_HEADERS = ['Risk', 'Description', 'Strategy', 'Created', ...(canApprove ? ['Action'] : [])];
    const pendingRows = pending.map((tp) => ({
        data: [
            riskId(tp.risk_id),
            <Body as="span" variant={2} color="var(--color-text-primary)">{tp.description || tp.title || '—'}</Body>,
            strategyBadge(tp.strategy),
            <Body as="span" variant={2} color="var(--color-text-secondary)">{fmtDate(tp.created_at)}</Body>,
            ...(canApprove ? [
                <CommonActionMenu
                    actions={[
                        { label: 'Approve', onClick: () => approve(tp) },
                        { label: 'Reject', onClick: () => reject(tp), danger: true },
                    ]}
                />,
            ] : []),
        ],
    }));

    return (
        <div className="rp1_treatments">
            <PageHeading
                title={mine ? 'My Treatment Plans' : 'Treatment Plans'}
                subtitle="Track treatment actions against their due dates and approvals."
            />

            {/* Overdue actions */}
            <div className="rp1_widget">
                <div className="rp1_widget_head"><CardTitle count={overdue.length}>Overdue Actions</CardTitle></div>
                {overdueRows.length === 0 ? (
                    <NoDataPage
                        title="All caught up"
                        hint="No treatment actions are overdue. Open a risk to manage its treatment plans."
                    />
                ) : (
                    <CommonTable headers={OVERDUE_HEADERS} rows={overdueRows} noPadding noScroll />
                )}
            </div>

            {/* Awaiting approval */}
            <div className="rp1_widget">
                <div className="rp1_widget_head"><CardTitle count={pending.length}>Awaiting Approval</CardTitle></div>
                {pendingRows.length === 0 ? (
                    <NoDataPage
                        title="Nothing awaiting approval"
                        hint="Treatment plans submitted for review will appear here."
                    />
                ) : (
                    <CommonTable headers={PENDING_HEADERS} rows={pendingRows} noPadding noScroll />
                )}
            </div>

            {busy && <CommonLoader fullPage label="Working…" />}
            {confirmModal}
            {toastNode}
        </div>
    );
}
